import { useEffect, useState, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import styles from './hypothetical-growth-chart.module.scss';
import Row from 'react-bootstrap/Row';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import format from '../../../../utils/format';
import chartHelpers from '../../../../utils/chart-helpers/index';
import { Crosshair, ModchartsInteractive } from '@markit/modcharts';
import { connect } from 'react-redux';
import { fetchHypoChartData } from '../../../../stores/screener/actions/chart-actions';
import useIntersection from '../../../../hooks/use-intersection';
import screenerAppContext from '../../../../stores/screener/screener-app-context';
import TableBasic from '../../../atoms/table-basic';
import { isRealEstate } from '../../../../utils/sharedHelpers';
import CrosshairTooltips from '../../../atoms/chart-crosshair-tooltips';
import chartLineColorsByColorCode from '../../../../helpers/statics/chart-color';

const className = augmentClassName(styles);

const HypotheticalGrowthChart = ({
  productType,
  xid,
  getHypoChartData,
  hypoChartData,
  chartLoading,
  inceptionDate,
  isCIT,
  assetClass,
  disclaimerSuperscript
}) => {
  const { yyy, channel } = useContext(screenerAppContext);
  const [hypoChartObject, setHypoChartObject] = useState();
  // eslint-disable-next-line no-unused-vars
  const [crossHairData, setCrossHairData] = useState();
  const [showNoData, setShowNoData] = useState(false);
  const [containerRef, setContainerRef] = useState(null);
  const [srHypo10kTableData, setSrHypo10kTableData] = useState(false);
  const isFirstInView = useRef(true);
  const inViewport = useIntersection(containerRef, '50px');

  //This function acts as a reference to the container element
  //It updates whenever the element becomes available in the DOM
  const handleContainerRef = element => {
    //if statement prevents infinite loop
    if (element !== containerRef) {
      setContainerRef(element);
    }
  };

  const loadChart = ({ funds, type, chartObject }) => {
    const baseChartParams = chartHelpers.formatBaseChartParams(funds, type);
    chartObject.load(baseChartParams);
  };

  if (inViewport && isFirstInView.current) {
    isFirstInView.current = false;
    getHypoChartData(xid, yyy, channel, inceptionDate, isCIT);
  }

  const getSrTableData = (fund = { growth: [] }) => {
    return {
      headers: [fund.name],
      rows: fund.growth
    };
  };

  useEffect(() => {
    if (chartLoading[xid] === false && !hypoChartObject) {
      const hypoChart = new ModchartsInteractive(`#hypo-chart-target${xid}`);
      setHypoChartObject(hypoChart);
      const crosshair = new Crosshair(hypoChart, {
        enabled: true
      });
      setCrossHairData(crosshair);
      let funds = hypoChartData[xid]?.funds;

      if (isRealEstate(assetClass)) {
        funds = funds?.filter(fund => fund?.legendName !== 'Category Average');
      }

      if (funds?.length) {
        setSrHypo10kTableData(getSrTableData(funds[0]));
        loadChart({
          funds,
          type: 'hypo-chart',
          chartObject: hypoChart
        });
      } else {
        setShowNoData(true);
      }
    }
  }, [chartLoading[xid]]); // eslint-disable-line react-hooks/exhaustive-deps

  const chartAvailable = hypoChartData[xid];

  return (
    <div {...className('container')} ref={handleContainerRef}>
      <Row {...className('header')}>
        <div>
          <span {...className('productType')}>{productType}</span> - Hypothetical Growth of $10k{' '}
          <sup>{disclaimerSuperscript}</sup>
          <span {...className('ml-5', 'note')}>
            As of {format.date(hypoChartData[xid]?.endDate)}
          </span>
        </div>
        {chartAvailable && (
          <div {...className('note')}>&#40;{hypoChartData[xid]?.timeFrame}&#41;</div>
        )}
      </Row>
      {!isRealEstate(assetClass) && (
        <Row>
          {hypoChartData[xid]?.funds?.map((fund, fundIndex) => {
            return (
              <div
                {...className('ml-3')}
                data-key={`${fund.legendName || ''}-${fundIndex}`}
                key={`${fund.legendName || ''}-${fundIndex}`}
              >
                <div
                  {...className(
                    'label-dot',
                    'mr-2',
                    `bg-${chartLineColorsByColorCode[fund.chartColor]}`
                  )}
                />
                <div {...className('d-inline-block', 'fund-names', 'label')}>{fund.legendName}</div>
              </div>
            );
          })}
        </Row>
      )}
      {!showNoData ? (
        <div {...className('relative')}>
          <div {...className('chart')} id={`hypo-chart-target${xid}`}></div>
          <CrosshairTooltips chart={hypoChartObject} chartType="hypoChart" />
          <div {...className('sr-only')}>
            <TableBasic>
              <caption>Hypothetical growth table</caption>
              <thead>
                <tr>
                  <th {...className('text-left')}>Date</th>
                  {srHypo10kTableData?.headers?.map((h, index) => (
                    <th
                      data-key={`${h || ''}-${index}`}
                      key={`${h || ''}-${index}`}
                      {...className('text-left')}
                    >
                      {format.text(h)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {srHypo10kTableData?.rows?.map((row, index) => (
                  <tr data-key={`${row.date || ''}-${index}`} key={`${row.date || ''}-${index}`}>
                    <th scope="row" {...className('text-left')}>
                      {row.date}
                    </th>
                    <td>{format.number(row.value)}</td>
                  </tr>
                ))}
              </tbody>
            </TableBasic>
          </div>
        </div>
      ) : (
        <div {...className('no-data')}>
          <div {...className('content')}>
            There is not enough data to create a Hypothetical Growth of 10k chart or other
            visualization for this product.
          </div>
        </div>
      )}
      {chartLoading[xid] && <div {...className('spinner')} />}
    </div>
  );
};

HypotheticalGrowthChart.propTypes = {
  productType: PropTypes.string,
  xid: PropTypes.number,
  getHypoChartData: PropTypes.func,
  hypoChartData: PropTypes.object,
  chartLoading: PropTypes.object,
  inceptionDate: PropTypes.string,
  isCIT: PropTypes.bool,
  assetClass: PropTypes.string,
  disclaimerSuperscript: PropTypes.string
};

const mapDispatchToProps = dispatch => {
  return {
    getHypoChartData: (xid, yyy, channel, inceptionDate, isCIT) =>
      dispatch(fetchHypoChartData(xid, yyy, channel, inceptionDate, isCIT))
  };
};

const mapStateToProps = state => {
  return {
    hypoChartData: state.chartReducer.hypoChartData,
    chartLoading: state.chartReducer.chartLoading
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(HypotheticalGrowthChart);
